import { computed } from 'vue'

import type { ComputedRef, Ref } from 'vue'
import type { KanbanCommentTask } from '@entities/kanban-comment'

import { useKanbanCommentsStore } from '@entities/kanban-comment'
import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute, TasksKanbanRelatedToType } from '@shared/config'

interface KanbanCommentTaskMetaOptions {
  task: Ref<KanbanCommentTask | undefined> | ComputedRef<KanbanCommentTask | undefined>
}

interface KanbanCommentTaskMetaReturn {
  refRelatedToLabel: ComputedRef<string>
  refRelatedToUrl: ComputedRef<string>
  refTaskLabel: ComputedRef<string>
  refTaskUrl: ComputedRef<string>
}

interface RelatedToRouteConfig {
  path: AppRoute
  label: string
}

const routeConfigByType: Record<TasksKanbanRelatedToType, RelatedToRouteConfig> = {
  [TasksKanbanRelatedToType.Customer]: {
    path: AppRoute.CustomersShow,
    label: 'Customer',
  },
  [TasksKanbanRelatedToType.Discipline]: {
    path: AppRoute.DisciplinesGeneralInfo,
    label: 'Discipline',
  },
  [TasksKanbanRelatedToType.Subscription]: {
    path: AppRoute.CustomersSubscriptions,
    label: 'Subscription',
  },
}

export const useKanbanCommentTaskMeta = ({ task }: KanbanCommentTaskMetaOptions): KanbanCommentTaskMetaReturn => {
  const listsStore = useListsStore()
  const kanbanCommentsStore = useKanbanCommentsStore()

  const refRelatedToLabel = computed<string>(() => {
    const key = task.value?.related_to_type
    const id = task.value?.related_to?.id
    if (!key) return ''
    const related =  listsStore.getTasksKanbanList?.related_to_type[key] ?? key
    return id ? `${related} #${id}` : related
  })

  const refRelatedToUrl = computed<string>(() => {
    const relatedToType = task.value?.related_to_type as TasksKanbanRelatedToType
    const id = task.value?.related_to?.id

    const config = routeConfigByType[relatedToType]

    if (relatedToType === TasksKanbanRelatedToType.Subscription) {
      const customerId = kanbanCommentsStore.getCustomerId
      if (customerId === null) return ''
      return resolveUrl(config.path, { id: customerId }) ?? ''
    }

    return resolveUrl(config.path, { id }) ?? ''
  })

  const refTaskLabel = computed<string>(() => {
    if (!task.value) return ''
    const typeKey = task.value.task_type
    const typeLabel = listsStore.getTasksKanbanList?.task_type[typeKey] ?? typeKey
    return `Task #${task.value.id} (${typeLabel})`
  })

  const refTaskUrl = computed<string>(() => {
    if (!task.value?.id) return ''
    return resolveUrl(AppRoute.TasksKanbanInfo, { id: task.value.id }) ?? ''
  })

  return {
    refRelatedToLabel,
    refRelatedToUrl,
    refTaskLabel,
    refTaskUrl,
  }
}
